import type { PromptArm } from './promptAnalytics'
import { PROMPT_FLAGS } from './promptExperiment'
import type { PromptFlag } from './promptExperiment'
import type { PromptPhone, PromptSurface } from './promptSurface'

/**
 * The prompt to show, decided from the surface and the visitor's arm.
 *
 * Every surface sits behind its Amplitude flag: route (`a` / `b`) and
 * desktop (`v1` / `v2`) are the two experiments, home is a plain on/off
 * flag. An arm the flag does not name — `off`, an unknown key, a failed
 * request — shows nothing: the flag is the kill switch, so no answer means
 * no prompt rather than a guessed arm that would pollute the comparison.
 */
export type PromptChoice =
  | { readonly kind: 'home'; readonly phone: PromptPhone; readonly arm: PromptArm }
  | {
      readonly kind: 'route'
      readonly phone: PromptPhone
      readonly variant: 'a' | 'b'
      readonly destination: string
      readonly arm: PromptArm
    }
  | { readonly kind: 'desktop'; readonly variant: 'v1' | 'v2'; readonly arm: PromptArm }

export async function choosePrompt(
  surface: PromptSurface,
  readVariant: (flag: PromptFlag) => Promise<string | undefined>,
): Promise<PromptChoice | undefined> {
  switch (surface.kind) {
    case 'home': {
      const variant = await readVariant(PROMPT_FLAGS.homePhone)
      if (variant !== 'on') return undefined
      return {
        kind: 'home',
        phone: surface.phone,
        arm: { surface: 'home', experiment: '', variant },
      }
    }
    case 'route': {
      const variant = await readVariant(PROMPT_FLAGS.routePhone)
      if (variant !== 'a' && variant !== 'b') return undefined
      return {
        kind: 'route',
        phone: surface.phone,
        variant,
        destination: surface.destination,
        arm: { surface: 'route', experiment: PROMPT_FLAGS.routePhone, variant },
      }
    }
    case 'desktop': {
      const variant = await readVariant(PROMPT_FLAGS.desktop)
      if (variant !== 'v1' && variant !== 'v2') return undefined
      return {
        kind: 'desktop',
        variant,
        arm: { surface: 'desktop', experiment: PROMPT_FLAGS.desktop, variant },
      }
    }
  }
}

/**
 * `?app_prompt=home|route-a|route-b|desktop-1|desktop-2` — shows that prompt
 * on any host and device for design review, with no flag request, no
 * once-per-visit gate and no analytics.
 */
export const PREVIEW_PARAM = 'app_prompt'

const PREVIEW_DESTINATION = 'Oxford Circus'

export function previewChoice(
  search: string,
  phone: PromptPhone | undefined,
  destination: string | undefined,
): PromptChoice | undefined {
  const key = new URLSearchParams(search).get(PREVIEW_PARAM)
  const previewPhone = phone ?? 'ios'
  const arm = (surface: PromptArm['surface'], variant: string): PromptArm => ({
    surface,
    experiment: 'preview',
    variant,
  })
  switch (key) {
    case 'home':
      return { kind: 'home', phone: previewPhone, arm: arm('home', 'on') }
    case 'route-a':
    case 'route-b': {
      const variant = key === 'route-a' ? 'a' : 'b'
      return {
        kind: 'route',
        phone: previewPhone,
        variant,
        destination: destination ?? PREVIEW_DESTINATION,
        arm: arm('route', variant),
      }
    }
    case 'desktop-1':
      return { kind: 'desktop', variant: 'v1', arm: arm('desktop', 'v1') }
    case 'desktop-2':
      return { kind: 'desktop', variant: 'v2', arm: arm('desktop', 'v2') }
    default:
      return undefined
  }
}
